import React, { useState, useRef, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Columns3, GripVertical, SlidersHorizontal,
} from 'lucide-react';

// ============ SORTABLE HEADER ============
const SortableHeader = ({ column, sortConfig, onSort, children, className = '' }) => {
  const isActive = sortConfig?.key === column;
  const direction = isActive ? sortConfig.direction : null;

  return (
    <button
      data-testid={`sort-header-${column}`}
      className={`flex items-center gap-1 w-full text-left font-medium hover:text-slate-900 transition-colors group ${className}`}
      onClick={() => onSort(column)}
    >
      <span className="truncate">{children}</span>
      <span className={`flex-shrink-0 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}>
        {direction === 'asc' ? (
          <ArrowUp size={14} className="text-purple-600" />
        ) : direction === 'desc' ? (
          <ArrowDown size={14} className="text-purple-600" />
        ) : (
          <ArrowUpDown size={14} />
        )}
      </span>
    </button>
  );
};

// ============ RESIZABLE COLUMN HANDLE ============
const ResizeHandle = ({ onResize, columnIndex }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    const th = e.target.closest('th');
    startWidthRef.current = th ? th.offsetWidth : 100;

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(60, startWidthRef.current + diff);
      onResize(columnIndex, newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnIndex, onResize]);

  return (
    <div
      data-testid={`resize-handle-${columnIndex}`}
      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-purple-400 active:bg-purple-500 z-10"
      onMouseDown={handleMouseDown}
    />
  );
};

// ============ COLUMN FILTER POPOVER ============
const ColumnFilter = ({ column, filterValue, onFilterChange, filterType = 'text', options = [] }) => {
  const hasValue = filterValue && filterValue !== '' && filterValue !== 'all';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`column-filter-${column}`}
          className={`p-0.5 rounded transition-colors ${hasValue ? 'text-purple-600 bg-purple-100' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <Filter size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3 bg-white" align="start">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-700">Filtrar</span>
            {hasValue && (
              <button
                onClick={() => onFilterChange(column, '')}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <X size={10} />Limpiar
              </button>
            )}
          </div>
          {filterType === 'select' ? (
            <Select value={filterValue || 'all'} onValueChange={(v) => onFilterChange(column, v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {options.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : filterType === 'date' ? (
            <div className="space-y-1">
              <Input type="date" className="h-8 text-xs"
                value={filterValue?.from || ''}
                onChange={(e) => onFilterChange(column, { ...filterValue, from: e.target.value })}
                placeholder="Desde" />
              <Input type="date" className="h-8 text-xs"
                value={filterValue?.to || ''}
                onChange={(e) => onFilterChange(column, { ...filterValue, to: e.target.value })}
                placeholder="Hasta" />
            </div>
          ) : (
            <Input
              className="h-8 text-xs"
              value={filterValue || ''}
              onChange={(e) => onFilterChange(column, e.target.value)}
              placeholder="Buscar..."
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ============ COLUMN VISIBILITY TOGGLE ============
const ColumnVisibilityToggle = ({ columns, visibleColumns, onToggle }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" data-testid="column-visibility-toggle" className="h-8 text-xs gap-1">
        <Columns3 size={14} />Columnas
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-52 p-3 bg-white" align="end">
      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-700 mb-2">Columnas visibles</p>
        {columns.filter(c => !c.alwaysVisible).map(col => (
          <label key={col.key} className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-slate-50 px-1 rounded">
            <input
              type="checkbox"
              checked={visibleColumns.includes(col.key)}
              onChange={() => onToggle(col.key)}
              className="rounded border-slate-300"
            />
            {col.label}
          </label>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

// ============ ACTIVE FILTERS BAR ============
const ActiveFilters = ({ columnFilters, columns, onClearFilter, onClearAll }) => {
  const activeFilters = Object.entries(columnFilters).filter(([_, v]) => v && v !== '' && v !== 'all');
  if (activeFilters.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="active-filters-bar">
      <SlidersHorizontal size={14} className="text-slate-400" />
      {activeFilters.map(([key, value]) => {
        const col = columns.find(c => c.key === key);
        const displayValue = typeof value === 'object'
          ? `${value.from || '...'} - ${value.to || '...'}`
          : col?.filterOptions?.find(o => o.value === value)?.label || value;
        return (
          <Badge key={key} variant="secondary"
            className="bg-purple-50 text-purple-700 border border-purple-200 gap-1 text-xs font-normal">
            {col?.label}: {displayValue}
            <button onClick={() => onClearFilter(key)} className="hover:text-purple-900">
              <X size={12} />
            </button>
          </Badge>
        );
      })}
      <button onClick={onClearAll} className="text-xs text-slate-500 hover:text-red-600 ml-1">
        Limpiar todos
      </button>
    </div>
  );
};

// ============ MAIN DATATABLE COMPONENT ============
export const DataTable = ({
  columns,
  data,
  loading = false,
  emptyIcon = null,
  emptyMessage = 'No hay datos',
  sortConfig = null,
  onSort = null,
  columnFilters = {},
  onColumnFilterChange = null,
  headerExtra = null,
  checkboxSelection = false,
  selectedIds = [],
  onToggleSelect = null,
  onToggleSelectAll = null,
  onRowClick = null,
  rowClassName = null,
  stickyHeader = true,
  initialColumnWidths = {},
  colSpanCount = null,
}) => {
  const [columnWidths, setColumnWidths] = useState(initialColumnWidths);
  const [visibleColumns, setVisibleColumns] = useState(
    columns.filter(c => c.defaultVisible !== false).map(c => c.key)
  );

  const handleResize = useCallback((colIndex, newWidth) => {
    const visibleCols = columns.filter(c => visibleColumns.includes(c.key) || c.alwaysVisible);
    const col = visibleCols[colIndex];
    if (col) {
      setColumnWidths(prev => ({ ...prev, [col.key]: newWidth }));
    }
  }, [columns, visibleColumns]);

  const toggleColumnVisibility = useCallback((key) => {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  const clearFilter = useCallback((key) => {
    onColumnFilterChange?.(key, '');
  }, [onColumnFilterChange]);

  const clearAllFilters = useCallback(() => {
    Object.keys(columnFilters).forEach(key => {
      onColumnFilterChange?.(key, '');
    });
  }, [columnFilters, onColumnFilterChange]);

  const displayColumns = columns.filter(c => c.alwaysVisible || visibleColumns.includes(c.key));
  const totalColSpan = colSpanCount || (displayColumns.length + (checkboxSelection ? 1 : 0));

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <ActiveFilters
          columnFilters={columnFilters}
          columns={columns}
          onClearFilter={clearFilter}
          onClearAll={clearAllFilters}
        />
        <div className="flex items-center gap-2 ml-auto">
          {headerExtra}
          <ColumnVisibilityToggle
            columns={columns}
            visibleColumns={visibleColumns}
            onToggle={toggleColumnVisibility}
          />
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-auto">
        <Table>
          <TableHeader className={stickyHeader ? 'sticky top-0 z-10' : ''}>
            <TableRow className="bg-slate-50/95 backdrop-blur-sm">
              {checkboxSelection && (
                <TableHead className="w-10" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    data-testid="select-all-checkbox"
                    checked={data.length > 0 && selectedIds.length === data.length}
                    onChange={onToggleSelectAll}
                    className="rounded border-slate-300"
                  />
                </TableHead>
              )}
              {displayColumns.map((col, idx) => (
                <TableHead
                  key={col.key}
                  className="relative select-none"
                  style={columnWidths[col.key] ? { width: columnWidths[col.key], minWidth: columnWidths[col.key] } : col.width ? { width: col.width, minWidth: col.minWidth || col.width } : {}}
                >
                  <div className="flex items-center gap-1">
                    {col.sortable && onSort ? (
                      <SortableHeader column={col.sortKey || col.key} sortConfig={sortConfig} onSort={onSort} className={col.headerClassName}>
                        {col.label}
                      </SortableHeader>
                    ) : (
                      <span className={`truncate ${col.headerClassName || ''}`}>{col.label}</span>
                    )}
                    {col.filterable && onColumnFilterChange && (
                      <ColumnFilter
                        column={col.key}
                        filterValue={columnFilters[col.key]}
                        onFilterChange={onColumnFilterChange}
                        filterType={col.filterType || 'text'}
                        options={col.filterOptions || []}
                      />
                    )}
                  </div>
                  {col.resizable !== false && (
                    <ResizeHandle columnIndex={idx + (checkboxSelection ? 1 : 0)} onResize={(_, w) => {
                      setColumnWidths(prev => ({ ...prev, [col.key]: w }));
                    }} />
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={totalColSpan} className="text-center py-8 text-slate-500">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                    Cargando...
                  </div>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColSpan} className="text-center py-8 text-slate-500">
                  {emptyIcon && <div className="mx-auto mb-2 text-slate-300">{emptyIcon}</div>}
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIdx) => (
                <TableRow
                  key={row.id || rowIdx}
                  className={`${onRowClick ? 'cursor-pointer' : ''} ${rowClassName ? rowClassName(row) : 'hover:bg-slate-50'}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {checkboxSelection && (
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => onToggleSelect?.(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-slate-300"
                        data-testid={`row-select-${row.id || rowIdx}`}
                      />
                    </TableCell>
                  )}
                  {displayColumns.map(col => (
                    <TableCell
                      key={col.key}
                      className={col.cellClassName || ''}
                      style={columnWidths[col.key] ? { maxWidth: columnWidths[col.key] } : {}}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTable;
